import type { ClientErrorPresentation } from "@dutyon/apis";

export type CompanyAdminAction = "ADD" | "REMOVE";

export interface CompanyAdminErrorFeedback {
  title: string;
  message: string;
  shouldRefresh: boolean;
}

function withRequestId(message: string, requestId?: string) {
  return requestId ? `${message} (문의 번호: ${requestId})` : message;
}

export function getCompanyAdminErrorFeedback(
  error: ClientErrorPresentation,
  action: CompanyAdminAction,
): CompanyAdminErrorFeedback {
  const isAdding = action === "ADD";

  if (error.kind === "FORBIDDEN") {
    return {
      title: isAdding ? "관리자 추가 권한 없음" : "관리자 해제 권한 없음",
      message: withRequestId(
        `현재 계정에는 이 병원의 관리자를 ${isAdding ? "추가할" : "해제할"} 권한이 없습니다. 로그인 계정과 병원 정보를 확인해 주세요.`,
        error.requestId,
      ),
      shouldRefresh: false,
    };
  }

  if (error.kind === "NOT_FOUND") {
    return {
      title: isAdding ? "소속 사용자 확인 필요" : "관리자 정보 없음",
      message: withRequestId(
        isAdding
          ? "해당 병원에 소속된 사용자를 찾을 수 없습니다. 먼저 사용자의 병원 연결 상태를 확인해 주세요."
          : "이미 해제되었거나 현재 병원 관리자가 아닌 사용자입니다. 관리자 목록을 새로 확인해 주세요.",
        error.requestId,
      ),
      shouldRefresh: true,
    };
  }

  if (error.kind === "CONFLICT" && error.errorCode === "LAST_COMPANY_ADMIN") {
    return {
      title: "마지막 관리자 해제 불가",
      message: withRequestId(
        "병원에는 최소 한 명의 관리자가 필요합니다. 다른 관리자를 추가한 뒤 다시 시도해 주세요.",
        error.requestId,
      ),
      shouldRefresh: true,
    };
  }

  if (error.kind === "CONFLICT") {
    return {
      title: "관리자 상태 확인 필요",
      message: withRequestId(error.message, error.requestId),
      shouldRefresh: true,
    };
  }

  return {
    title: isAdding ? "관리자 추가 실패" : "관리자 해제 실패",
    message: withRequestId(error.message, error.requestId),
    shouldRefresh: false,
  };
}
